// src/store/slices/userManagementSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../services/api/axios';

// API Routes
const API_ROUTES = {
  GET_ALL_USERS: '/user-management',
  GET_USER: '/user-management',
  UPDATE_USER: '/user-management',
  ACTIVATE_USER: '/user-management',
  DEACTIVATE_USER: '/user-management',
  VERIFY_USER_PHONE: '/user-management',
  RESET_VERIFICATION: '/user-management',
  GET_USER_STATS: '/user-management/stats',
  EXPORT_USERS: '/user-management/export',
  GET_USER_ACTIVITY_LOGS: '/user-management',
  SEARCH_USERS: '/user-management/search'
};

// Thunk Actions
export const getAllUsers = createAsyncThunk(
  'userManagement/getAllUsers',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { page = 1, limit = 10, search = '', isActive = '' } = params;
      const queryParams = new URLSearchParams();
      
      queryParams.append('page', page);
      queryParams.append('limit', limit);
      
      if (search) queryParams.append('search', search);
      if (isActive !== '') queryParams.append('isActive', isActive);
      
      const response = await axiosInstance.get(`${API_ROUTES.GET_ALL_USERS}?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch users');
    }
  }
);

export const getUserById = createAsyncThunk(
  'userManagement/getUserById',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${API_ROUTES.GET_USER}/${userId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch user details');
    }
  }
);

export const updateUser = createAsyncThunk(
  'userManagement/updateUser',
  async ({ userId, data }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`${API_ROUTES.UPDATE_USER}/${userId}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update user');
    }
  }
);

export const activateUser = createAsyncThunk(
  'userManagement/activateUser',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`${API_ROUTES.ACTIVATE_USER}/${userId}/activate`);
      return { ...response.data, userId };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to activate user');
    }
  }
);

export const deactivateUser = createAsyncThunk(
  'userManagement/deactivateUser',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`${API_ROUTES.DEACTIVATE_USER}/${userId}/deactivate`);
      return { ...response.data, userId };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to deactivate user');
    }
  }
);

export const verifyUserPhone = createAsyncThunk(
  'userManagement/verifyUserPhone',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`${API_ROUTES.VERIFY_USER_PHONE}/${userId}/verify-phone`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to verify user phone');
    }
  }
);

export const resetUserVerification = createAsyncThunk(
  'userManagement/resetUserVerification',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`${API_ROUTES.RESET_VERIFICATION}/${userId}/reset-verification`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to reset verification');
    }
  }
);

export const getUserStats = createAsyncThunk(
  'userManagement/getUserStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(API_ROUTES.GET_USER_STATS);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to get user statistics');
    }
  }
);

export const exportUsers = createAsyncThunk(
  'userManagement/exportUsers',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { isActive, dateFrom, dateTo } = params;
      const queryParams = new URLSearchParams();
      
      if (isActive !== '') queryParams.append('isActive', isActive);
      if (dateFrom) queryParams.append('dateFrom', dateFrom);
      if (dateTo) queryParams.append('dateTo', dateTo);
      
      const response = await axiosInstance.get(`${API_ROUTES.EXPORT_USERS}?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to export users');
    }
  }
);

export const getUserActivityLogs = createAsyncThunk(
  'userManagement/getUserActivityLogs',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${API_ROUTES.GET_USER_ACTIVITY_LOGS}/${userId}/activity-logs`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to get user activity logs');
    }
  }
);

export const searchUsers = createAsyncThunk(
  'userManagement/searchUsers',
  async (query, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${API_ROUTES.SEARCH_USERS}?query=${query}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to search users');
    }
  }
);

const initialState = {
  users: [],
  currentUser: null,
  stats: null,
  exportedUsers: [],
  activityLogs: [],
  searchResults: [],
  pagination: null,
  loading: false,
  error: null,
  success: false
};

const userManagementSlice = createSlice({
  name: 'userManagement',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
    },
    resetUserState: () => initialState
  },
  extraReducers: (builder) => {
    builder
      // Get All Users
      .addCase(getAllUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload.data.users;
        state.pagination = action.payload.data.pagination;
      })
      .addCase(getAllUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch users';
      })

      // Get User By Id
      .addCase(getUserById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentUser = action.payload.data.user;
      })
      .addCase(getUserById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch user details';
      })

      // Update User
      .addCase(updateUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        const updatedUser = action.payload.data;
        
        // Update in users list if present
        state.users = state.users.map(user => 
          user.userId === updatedUser.userId ? updatedUser : user
        );
        
        // Update current user if it's the same one
        if (state.currentUser?.userId === updatedUser.userId) {
          state.currentUser = updatedUser;
        }
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to update user';
      })

      // Activate User
      .addCase(activateUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(activateUser.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        const userId = action.payload.userId;
        
        // Update isActive status in the users array
        state.users = state.users.map(user => 
          user.userId === userId ? { ...user, isActive: true } : user
        );
        
        // Update current user if it's the same one
        if (state.currentUser?.userId === userId) {
          state.currentUser = { ...state.currentUser, isActive: true };
        }
      })
      .addCase(activateUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to activate user';
      })

      // Deactivate User
      .addCase(deactivateUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deactivateUser.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        const userId = action.payload.userId;
        
        // Update isActive status in the users array
        state.users = state.users.map(user => 
          user.userId === userId ? { ...user, isActive: false } : user
        );
        
        // Update current user if it's the same one
        if (state.currentUser?.userId === userId) {
          state.currentUser = { ...state.currentUser, isActive: false };
        }
      })
      .addCase(deactivateUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to deactivate user';
      })

      // Verify User Phone
      .addCase(verifyUserPhone.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(verifyUserPhone.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        const updatedUser = action.payload.data;
        
        // Update in users list if present
        state.users = state.users.map(user => 
          user.userId === updatedUser.userId ? {
            ...user,
            phone: { ...user.phone, verified: true }
          } : user
        );
        
        // Update current user if it's the same one
        if (state.currentUser?.userId === updatedUser.userId) {
          state.currentUser = {
            ...state.currentUser,
            phone: { ...state.currentUser.phone, verified: true }
          };
        }
      })
      .addCase(verifyUserPhone.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to verify user phone';
      })

      // Reset User Verification
      .addCase(resetUserVerification.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(resetUserVerification.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        const updatedUser = action.payload.data;
        
        // Update in users list if present
        state.users = state.users.map(user => 
          user.userId === updatedUser.userId ? {
            ...user,
            phone: { ...user.phone, verified: false }
          } : user
        );
        
        // Update current user if it's the same one
        if (state.currentUser?.userId === updatedUser.userId) {
          state.currentUser = {
            ...state.currentUser,
            phone: { ...state.currentUser.phone, verified: false }
          };
        }
      })
      .addCase(resetUserVerification.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to reset verification';
      })

      // Get User Stats
      .addCase(getUserStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload.data.stats;
      })
      .addCase(getUserStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to get user statistics';
      })

      // Export Users
      .addCase(exportUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(exportUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.exportedUsers = action.payload.data.users;
      })
      .addCase(exportUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to export users';
      })

      // Get User Activity Logs
      .addCase(getUserActivityLogs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserActivityLogs.fulfilled, (state, action) => {
        state.loading = false;
        state.currentUser = action.payload.data;
        state.activityLogs = action.payload.data.logs;
      })
      .addCase(getUserActivityLogs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch user activity logs';
      })

      // Search Users
      .addCase(searchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.searchResults = action.payload.data.users;
      })
      .addCase(searchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to search users';
      });
  }
});

export const { clearError, clearSuccess, resetUserState } = userManagementSlice.actions;
export default userManagementSlice.reducer;